import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Intent } from '@blueprintjs/core';
import { Modal, ModalSize, NotificationToaster } from 'components/notifications';
import { FormDragAndDropFileUploader, FormTextArea } from 'components/form-fields';
import { Button, ButtonSave } from 'components/buttons';
import { ShowHide } from 'components/layout';
import { useValidation } from 'hooks/useValidation';
import { usePageTitle } from 'hooks/usePageTitle';
import { FeedbackService } from 'services';
import { SubmissionPrivacyNotice } from './SubmissionPrivacyNotice';
import { SubmissionContactSection, contactNameMaxLength, phoneNumberMaxLength } from './SubmissionContactSection';

const defaultFunction = () => {};
const descriptionMaxLength = 4000;
const badRequestCode = 400;
const maxAttachmentCount = 10;
const maxAttachmentSizeBytes = 2 * 1024 * 1024;
const allowedAttachmentExtensions = [".jpg", ".jpeg", ".png", ".gif", ".csv", ".xls", ".xlsx", ".doc", ".docx", ".pdf"];
const descriptionHelperText = "Describe the issue you are having, please include replication steps and as much information as possible.";
const attachmentLimitMessage = "No more than ten files can be attached.";

const validationRules = [
    { fieldName: "description", required: true, maxLength: descriptionMaxLength },
    { fieldName: "contactName", maxLength: contactNameMaxLength },
    { fieldName: "phoneNumber", maxLength: phoneNumberMaxLength }
];

const defaultIssueReport = {
    description: "",
    contactName: "",
    phoneNumber: ""
};

export function ReportIssueModal(
    {
        isOpen = false,
        onClose = defaultFunction
    }
) {
    const source = usePageTitle();
    const [issueReport, setIssueReport] = useState(defaultIssueReport);
    const [attachments, setAttachments] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [isValid, errors, validate] = useValidation();

    useEffect(runValidation, [issueReport]);
    useEffect(resetOnClose, [isOpen]);

    function runValidation() {
        validate(validationRules, issueReport);
    }

    function resetOnClose() {
        if (!isOpen) {
            setIssueReport(defaultIssueReport);
            setAttachments([]);
            setSubmitted(false);
        }
    }

    function onFieldChange(fieldName, value) {
        setIssueReport({
            ...issueReport,
            [fieldName]: value
        });
    }

    function onFilesAdded(newFiles) {
        const remainingSlots = maxAttachmentCount - attachments.length;
        let filesToAdd = newFiles;

        if (newFiles.length > remainingSlots) {
            filesToAdd = newFiles.slice(0, remainingSlots);
            NotificationToaster.show(Intent.WARNING, attachmentLimitMessage);
        }

        setAttachments([...attachments, ...filesToAdd]);
    }

    function onFileRemoved(name) {
        setAttachments(attachments.filter((file) => file.name !== name));
    }

    function buildFormData() {
        const formData = new FormData();

        formData.append("source", source);
        formData.append("description", issueReport.description);
        formData.append("contactName", issueReport.contactName);
        formData.append("phoneNumber", issueReport.phoneNumber);

        attachments.forEach((file) => {
            formData.append("attachments", file, file.name);
        });

        return formData;
    }

    function onSubmitClick() {
        if (!isValid || submitting) {
            return;
        }

        setSubmitting(true);

        FeedbackService.submitIssueReport(buildFormData())
            .then(() => {
                setSubmitted(true);
            })
            .catch((error) => {
                if (error?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, "Your issue report could not be sent. Please try again.");
                }
            })
            .finally(() => {
                setSubmitting(false);
            });
    }

    let modalTitle = "Report an Issue";

    if (submitted) {
        modalTitle = "Thank you for reporting an issue";
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            clickOutsideClose={true}
            title={modalTitle}
            size={ModalSize.MEDIUM}
            updateStateOnClose={false}
            footer={(
                <ShowHide
                    evaluator={submitted}
                    show={(
                        <div className="bp6-dialog-footer-actions">
                            <Button
                                id="close-issue-button"
                                text="Close"
                                intent={Intent.PRIMARY}
                                onClick={onClose}
                            />
                        </div>
                    )}
                    hide={(
                        <div className="bp6-dialog-footer-actions">
                            <Button
                                id="cancel-issue-button"
                                text="Cancel"
                                intent={Intent.SECONDARY}
                                onClick={onClose}
                            />
                            <ButtonSave
                                id="submit-issue-button"
                                text="Submit"
                                onClick={onSubmitClick}
                                simpleDisabled={!isValid}
                                disabled={submitting}
                            />
                        </div>
                    )}
                />
            )}
        >
            <ShowHide
                evaluator={submitted}
                show={(
                    <p>Your message has been sent to the Vision development team.</p>
                )}
                hide={(
                    <Fragment>
                        <FormTextArea
                            id="issue-description"
                            headingText="Description"
                            helperText={descriptionHelperText}
                            value={issueReport.description}
                            maxLength={descriptionMaxLength}
                            showCounter={true}
                            disabled={submitting}
                            dangerHelperText={errors.description}
                            onChange={(event) => onFieldChange("description", event.target.value)}
                            fill={true}
                        />
                        <FormDragAndDropFileUploader
                            id="issue-attachments"
                            headingText="Attachments"
                            documents={attachments}
                            allowedFileTypes={allowedAttachmentExtensions}
                            maxFileSize={maxAttachmentSizeBytes}
                            showVersions={false}
                            smaller={true}
                            fixedTableHeight={false}
                            disabled={submitting}
                            onChange={onFilesAdded}
                            onRemoveDocument={onFileRemoved}
                        />
                        <SubmissionPrivacyNotice />
                        <SubmissionContactSection
                            contactName={issueReport.contactName}
                            phoneNumber={issueReport.phoneNumber}
                            idPrefix="issue"
                            errors={errors}
                            disabled={submitting}
                            onChange={onFieldChange}
                        />
                    </Fragment>
                )}
            />
        </Modal>
    );
}

ReportIssueModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func
};
